import { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { format, isValid, parseISO } from 'date-fns';
import { MobileFormPage } from '@/components/MobileFormPage';
import { SalidaForm, type SalidaFormValues } from '@/components/SalidaForm';
import { useToast } from '@/components/toastStore';
import { extractApiMessage } from '@/lib/api';
import { decimalSolicitud, entradaDecimal } from '@/lib/liquidacion';
import { Decimal } from '@/lib/offline/decimal';
import { currentBundle } from '@/lib/offline/bundle';
import { useEntityId, useEntityMappings } from '@/lib/offline/operations';
import {
  useActualizarPesoSalida,
  useActualizarSalida,
  useCrearSalida,
  useSalidasCamada,
  useContextoSalidas,
  type SalidaCreateInput,
} from '@/lib/queries/salidas';
import { useAuthStore } from '@/stores/auth';

function decimal(value: string) {
  try {
    return new Decimal(decimalSolicitud(value)).toString();
  } catch {
    return value.trim();
  }
}
export function SalidaFormPage({ pesoOnly = false }: { pesoOnly?: boolean }) {
  const { camadaId: parameter, salidaId: exitParameter } = useParams();
  const owner = useAuthStore((s) => s.user?.id ?? null);
  useEntityMappings();
  const farm = currentBundle()?.explotacion_id ?? null;
  const identity = `${owner}:${parameter}:${exitParameter}:${pesoOnly}`;
  const context = useRef({ identity, farm, revision: 0 });
  if (context.current.identity !== identity) context.current = { identity, farm, revision: 0 };
  else if (farm != null) {
    if (context.current.farm != null && context.current.farm !== farm) context.current.revision++;
    context.current.farm = farm;
  }
  return (
    <LoadForm
      key={`${identity}:${context.current.revision}`}
      parameter={parameter}
      exitParameter={exitParameter}
      pesoOnly={pesoOnly}
    />
  );
}
function LoadForm({
  parameter,
  exitParameter,
  pesoOnly,
}: {
  parameter: string | undefined;
  exitParameter: string | undefined;
  pesoOnly: boolean;
}) {
  const id = useEntityId(parameter, 'camadas'),
    exitId = useEntityId(exitParameter, 'salidas');
  const edit = exitParameter != null;
  const detailQ = useContextoSalidas(id),
    exitsQ = useSalidasCamada(id);
  const createQ = useCrearSalida(id ?? 0),
    updateQ = useActualizarSalida(id ?? 0),
    weightQ = useActualizarPesoSalida(id ?? 0);
  const user = useAuthStore((s) => s.user);
  const mappings = useEntityMappings();
  const mapId = (entity: string, value: number) =>
    mappings.find((m) => m.entity === entity && m.localId === value)?.remoteId ?? value;
  const owner = user?.id;
  const scope = useRef({ owner, farm: currentBundle()?.explotacion_id ?? null });
  if (scope.current.farm == null) scope.current.farm = currentBundle()?.explotacion_id ?? null;
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);
  const current = () =>
    mounted.current &&
    useAuthStore.getState().user?.id === scope.current.owner &&
    (scope.current.farm == null || currentBundle()?.explotacion_id === scope.current.farm);
  const detail = detailQ.data,
    camada = detail?.camada;
  const exit = exitsQ.data?.find((s) => s.id === exitId);
  const lotes = detail?.lotes ?? [];
  const ready =
    id != null &&
    !!camada &&
    !!exitsQ.data &&
    !detailQ.isError &&
    !exitsQ.isError &&
    (!edit || !!exit);
  const canWrite = user?.rol === 'admin' || user?.rol === 'ganadero' || user?.rol === 'operario';
  const canEdit =
    !!ready &&
    !camada?.cerrada &&
    !exit?.anulada &&
    canWrite &&
    (!edit || pesoOnly || user?.rol !== 'operario');
  const [draft, setDraft] = useState<SalidaFormValues | null>(null),
    [baseline, setBaseline] = useState<SalidaFormValues | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const today = format(new Date(), 'yyyy-MM-dd');
  useEffect(() => {
    if (!ready || draft) return;
    const initial: SalidaFormValues = {
      fecha: exit?.fecha_carga ?? today,
      matadero: exit?.matadero ?? '',
      camion: exit?.numero_camion ?? '',
      albaran: exit?.numero_albaran ?? '',
      observaciones: exit?.observaciones ?? '',
      pesoSalida: entradaDecimal(exit?.kg_confirmados_destino ?? null),
      fechaPesoSalida: exit?.fecha_recepcion_peso ?? '',
      pesoConocido: pesoOnly || exit?.kg_confirmados_destino != null,
      unidades: Object.fromEntries(
        (exit?.lineas ?? []).map((line) => [line.lote_nave_id, String(line.unidades_cargadas)]),
      ),
    };
    setDraft(initial);
    setBaseline(initial);
  }, [ready, draft, exit, today, pesoOnly]);
  const mapUnits = (values: SalidaFormValues) =>
    Object.fromEntries(
      Object.entries(values.unidades).map(([raw, value]) => [mapId('lotes', Number(raw)), value]),
    );
  function canonical(values: SalidaFormValues) {
    const units = mapUnits(values);
    return {
      fecha: values.fecha,
      matadero: values.matadero.trim(),
      camion: values.camion.trim(),
      albaran: values.albaran.trim(),
      observaciones: values.observaciones.trim(),
      known: values.pesoConocido,
      kg: values.pesoConocido ? decimal(values.pesoSalida) : '',
      date: values.pesoConocido ? values.fechaPesoSalida : '',
      unidades: Object.keys(units)
        .map(Number)
        .sort((a, b) => a - b)
        .filter((key) => units[key].trim() !== '' && Number(units[key]) !== 0)
        .map((key) => [key, decimal(units[key])]),
    };
  }
  const dirty =
    !!draft &&
    !!baseline &&
    JSON.stringify(canonical(draft)) !== JSON.stringify(canonical(baseline));
  const display = draft ? { ...draft, unidades: mapUnits(draft) } : null;
  const total = display
    ? Object.values(display.unidades).reduce(
        (sum, value) =>
          sum +
          (/^\d+$/.test(value.trim()) && Number.isSafeInteger(Number(value)) ? Number(value) : 0),
        0,
      )
    : 0;
  function change(field: Exclude<keyof SalidaFormValues, 'unidades'>, value: string | boolean) {
    setDraft((previous) => (previous ? { ...previous, [field]: value } : previous));
    setErrors((previous) => {
      const next = { ...previous };
      delete next[field];
      return next;
    });
  }
  function quantities(loteId: number, value: string) {
    setDraft((previous) => {
      if (!previous) return previous;
      const key =
        Object.keys(previous.unidades).find((raw) => mapId('lotes', Number(raw)) === loteId) ??
        String(loteId);
      return { ...previous, unidades: { ...previous.unidades, [key]: value } };
    });
    setErrors((previous) => {
      const next = { ...previous };
      delete next[`unidades-${loteId}`];
      delete next.unidades;
      return next;
    });
  }
  const toast = useToast();
  async function save() {
    if (!current() || !canEdit || !ready || !display || !id || (edit && !dirty)) return;
    const validation: Record<string, string> = {};
    const validDate = (value: string) =>
      /^\d{4}-\d{2}-\d{2}$/.test(value) && isValid(parseISO(value));
    const kg = decimal(display.pesoSalida);
    if (
      display.pesoConocido &&
      (!/^\d{1,10}(?:\.\d{1,2})?$/.test(kg) || new Decimal(kg).compare(0) <= 0)
    )
      validation.pesoSalida = 'Introduce un peso mayor que 0, con hasta dos decimales.';
    if (display.pesoConocido && display.fechaPesoSalida && !validDate(display.fechaPesoSalida))
      validation.fechaPesoSalida = 'Selecciona una fecha de recepción válida.';
    const lineas: SalidaCreateInput['lineas'] = [];
    if (!pesoOnly) {
      if (
        !validDate(display.fecha) ||
        display.fecha < camada!.fecha_entrada ||
        display.fecha > today
      )
        validation.fecha = 'La fecha de carga debe estar entre la entrada de la camada y hoy.';
      if (!display.matadero.trim() || display.matadero.trim().length > 200)
        validation.matadero = 'Indica un matadero, hasta 200 caracteres.';
      if (!display.camion.trim() && !display.albaran.trim())
        validation.camion = 'Indica el camión o el albarán de esta carga.';
      if (display.camion.trim().length > 100)
        validation.camion = 'El camión admite hasta 100 caracteres.';
      if (display.albaran.trim().length > 100)
        validation.albaran = 'El albarán admite hasta 100 caracteres.';
      for (const [raw, value] of Object.entries(display.unidades)) {
        const key = Number(raw),
          number = Number(value || 0);
        if (
          value.trim() &&
          (!/^\d+$/.test(value.trim()) ||
            !Number.isSafeInteger(number) ||
            number < 0 ||
            number > 2147483647)
        ) {
          validation[`unidades-${key}`] = 'Introduce un número entero de aves igual o mayor que 0.';
          continue;
        }
        if (number > 0) {
          if (!lotes.some((lote) => lote.lote_id === key))
            validation.unidades =
              'Una asignación ya no está disponible. Revisa la carga antes de guardar.';
          lineas.push({ lote_nave_id: key, unidades_cargadas: number });
        }
      }
      if (!lineas.length) validation.unidades = 'Indica animales en al menos una nave.';
    }
    setErrors(validation);
    if (Object.keys(validation).length) throw new Error(Object.values(validation).join(' '));
    const data: SalidaCreateInput = {
      fecha_carga: display.fecha,
      matadero: display.matadero.trim(),
      numero_camion: display.camion.trim() || undefined,
      numero_albaran: display.albaran.trim() || undefined,
      observaciones: display.observaciones.trim() || undefined,
      lineas,
      kg_confirmados_destino: display.pesoConocido ? kg : null,
      fecha_recepcion_peso:
        display.pesoConocido && display.fechaPesoSalida ? display.fechaPesoSalida : undefined,
    };
    await (pesoOnly
      ? weightQ.mutateAsync({
          salidaId: exitId!,
          kg_confirmados_destino: kg,
          fecha_recepcion_peso: display.fechaPesoSalida || undefined,
        })
      : edit
        ? updateQ.mutateAsync({ salidaId: exitId!, ...data })
        : createQ.mutateAsync(data));
    if (!current()) return;
    toast.show({
      kind: 'success',
      message: pesoOnly ? 'Peso guardado.' : 'Carga guardada.',
    });
    return `/camadas/${id}/salidas`;
  }
  return (
    <MobileFormPage
      title={pesoOnly ? 'Peso de destino' : edit ? 'Editar salida' : 'Salida a matadero'}
      backTo={`/camadas/${id}/salidas`}
      backLabel="Salidas"
      saveLabel={pesoOnly ? 'Guardar peso' : edit ? 'Guardar cambios' : 'Guardar carga'}
      dirty={dirty}
      disabled={!canEdit || !draft || (edit && !dirty)}
      onSave={save}
    >
      <p className="cm-form-context">
        {camada?.codigo_lote ?? 'Camada'}
        <span>
          {pesoOnly
            ? `${exit?.numero_camion || exit?.numero_albaran || 'Camión'} · ${exit?.unidades_cargadas_total ?? '—'} aves`
            : !camada
              ? ''
              : camada.sexo === 'machos'
                ? 'Machos'
                : 'Hembras'}
        </span>
      </p>
      {!ready && (
        <p
          role={
            detailQ.isError || exitsQ.isError || (edit && !!exitsQ.data && !exit)
              ? 'alert'
              : 'status'
          }
        >
          {detailQ.isError || exitsQ.isError
            ? `Carga no disponible. ${extractApiMessage(detailQ.error ?? exitsQ.error, 'Conecta para actualizar.')}`
            : edit && !!exitsQ.data && !exit
              ? 'Esta carga no pertenece a la camada.'
              : 'Cargando salida…'}
        </p>
      )}
      {ready && display && (
        <>
          {!canEdit && (
            <p className="cm-form-note">
              {camada!.cerrada
                ? 'La camada está cerrada. Puedes consultar esta carga.'
                : exit?.anulada
                  ? 'Esta carga está anulada.'
                  : 'Tu perfil permite consultar esta carga.'}
            </p>
          )}
          {edit && !pesoOnly && (
            <p className="cm-form-note">
              La corrección devuelve primero las aves de esta carga al saldo antes de validar las
              nuevas cantidades.
            </p>
          )}
          {!lotes.length && !pesoOnly && (
            <p role="alert">Asigna naves antes de registrar una carga.</p>
          )}
          <SalidaForm
            lotes={lotes}
            values={display}
            onChange={change}
            onUnidadesChange={quantities}
            errors={errors}
            disabled={!canEdit}
            pesoOnly={pesoOnly}
            fechaMin={camada!.fecha_entrada}
            fechaMax={today}
            total={pesoOnly ? exit!.unidades_cargadas_total : total}
          />
        </>
      )}
    </MobileFormPage>
  );
}
